const SETTINGS_KEY = "pos_printer_settings";
const ESC = 0x1b, GS = 0x1d;

export function getPrinterSettings() {
  try { return { width: 80, type: "bluetooth", ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}") }; }
  catch { return { width: 80, type: "bluetooth" }; }
}
export function savePrinterSettings(s) { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); }

let conn = null; // { type, name, write(bytes) }
export const printerConnected = () => !!conn;
export const printerName = () => conn?.name || "";
export const savedPrinterName = () => getPrinterSettings().deviceName || "";
export const supportsBluetooth = () => typeof navigator !== "undefined" && !!navigator.bluetooth;
export const supportsUSB = () => typeof navigator !== "undefined" && !!navigator.usb;

async function bindBluetooth(device) {
  const server = await device.gatt.connect();
  const services = await server.getPrimaryServices();
  let ch = null;
  for (const s of services) {
    const chars = await s.getCharacteristics();
    ch = chars.find((c) => c.properties.writeWithoutResponse || c.properties.write);
    if (ch) break;
  }
  if (!ch) throw new Error("Karakteristik tulis printer tidak ditemukan");
  device.addEventListener("gattserverdisconnected", () => { conn = null; });
  conn = {
    type: "bluetooth", name: device.name || "Printer BT",
    write: async (bytes) => {
      for (let i = 0; i < bytes.length; i += 100) {
        const chunk = bytes.slice(i, i + 100);
        if (ch.properties.writeWithoutResponse) await ch.writeValueWithoutResponse(chunk);
        else await ch.writeValue(chunk);
      }
    },
  };
  return conn.name;
}

export async function connectBluetooth() {
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: ["000018f0-0000-1000-8000-00805f9b34fb", "e7810a71-73ae-499d-8c15-faa9aef0c3f2", "49535343-fe7d-4ae5-8fa9-9fafd205e455"],
  });
  const name = await bindBluetooth(device);
  savePrinterSettings({ ...getPrinterSettings(), type: "bluetooth", deviceId: device.id, deviceName: name });
  return name;
}

async function bindUSB(device) {
  await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  const iface = device.configuration.interfaces.find((i) => i.alternates.some((a) => a.interfaceClass === 7)) || device.configuration.interfaces[0];
  await device.claimInterface(iface.interfaceNumber);
  const ep = iface.alternates[0].endpoints.find((e) => e.direction === "out");
  if (!ep) throw new Error("Endpoint printer tidak ditemukan");
  conn = {
    type: "usb", name: device.productName || "Printer USB",
    write: async (bytes) => { await device.transferOut(ep.endpointNumber, bytes); },
  };
  return conn.name;
}

export async function connectUSB() {
  const device = await navigator.usb.requestDevice({ filters: [{ classCode: 7 }, {}] });
  const name = await bindUSB(device);
  savePrinterSettings({ ...getPrinterSettings(), type: "usb", deviceName: name });
  return name;
}

// Reconnect silently to a previously paired printer (no user prompt) using
// the persisted browser device permission (getDevices).
export async function reconnectPrinter() {
  if (conn) return conn.name;
  const s = getPrinterSettings();
  try {
    if (s.type === "bluetooth" && s.deviceId && navigator.bluetooth?.getDevices) {
      const devices = await navigator.bluetooth.getDevices();
      const dev = devices.find((d) => d.id === s.deviceId);
      if (dev) return await bindBluetooth(dev);
    }
    if (s.type === "usb" && navigator.usb?.getDevices) {
      const devices = await navigator.usb.getDevices();
      const dev = devices.find((d) => (d.productName || "") === (s.deviceName || "")) || devices[0];
      if (dev) return await bindUSB(dev);
    }
  } catch { /* device out of range / not granted */ }
  return null;
}

export function disconnectPrinter() { conn = null; }

function buildReceipt(sale, cashier, width) {
  const cols = width === 58 ? 32 : 48;
  const enc = new TextEncoder();
  const out = [];
  const raw = (...b) => out.push(Uint8Array.from(b));
  const txt = (s) => out.push(enc.encode(s + "\n"));
  const lr = (l, r) => { const gap = Math.max(1, cols - l.length - r.length); txt(l + " ".repeat(gap) + r); };
  const line = () => txt("-".repeat(cols));
  const num = (n) => Math.round(n || 0).toLocaleString("id-ID");
  raw(ESC, 0x40);
  raw(ESC, 0x61, 1); raw(ESC, 0x45, 1);
  txt("TOKO BANGUNAN"); txt("SUMBER HARAPAN");
  raw(ESC, 0x45, 0); txt("Bitung, Sulawesi Utara");
  raw(ESC, 0x61, 0);
  txt(`No   : ${sale.number}`); txt(`Kasir: ${cashier}`);
  txt(`Tgl  : ${new Date(sale.created_at).toLocaleString("id-ID")}`);
  line();
  for (const l of sale.items) {
    txt(l.name.slice(0, cols));
    lr(`${l.qty} x ${num(l.price)}${l.discount ? ` -${num(l.discount)}` : ""}`, num(l.subtotal));
  }
  line();
  lr("Subtotal", num(sale.subtotal));
  if (sale.total_discount > 0) lr("Diskon", num(sale.total_discount));
  raw(ESC, 0x45, 1); lr("TOTAL", "Rp " + num(sale.total)); raw(ESC, 0x45, 0);
  lr("Bayar", num(sale.paid_amount)); lr("Kembali", num(sale.change));
  txt(`Metode: ${(sale.payment_method || "").toUpperCase()}`);
  line();
  raw(ESC, 0x61, 1); txt("Terima kasih atas kunjungan Anda");
  raw(ESC, 0x64, 3); raw(GS, 0x56, 0x00);
  const len = out.reduce((s, a) => s + a.length, 0);
  const buf = new Uint8Array(len); let o = 0;
  for (const a of out) { buf.set(a, o); o += a.length; }
  return buf;
}

export async function printThermal(sale, cashier) {
  if (!conn) return false;
  const { width } = getPrinterSettings();
  await conn.write(buildReceipt(sale, cashier, width));
  return true;
}
